import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/DashLayout";
import { api } from "@/lib/api";
import { Smartphone, Plus, Trash2, Edit2, LogIn, Wallet, Loader2, X, Star } from "lucide-react";

const OPERATORS = ["Dish TV"];

export default function SimDetails() {
  const [sims, setSims] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [otpModal, setOtpModal] = useState(null); // sim object when OTP modal is open
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [formData, setFormData] = useState({
    operator_type: "Dish TV", sim_number: "", sim_id: "", password: "",
    wallet_password: "", company_id: "1", organization: "DISHTV",
    is_primary_recharge: false, notes: ""
  });
  const [editId, setEditId] = useState(null);
  const [actionLoading, setActionLoading] = useState({}); // { [simId]: 'login' | 'balance' }

  const load = async () => {
    try {
      const res = await api.get("/admin/sim-details");
      setSims(res.data);
    } catch (e) {
      toast.error("Failed to load SIM details");
    }
  };

  useEffect(() => { load(); }, []);

  const openModal = (sim = null) => {
    if (sim) {
      setEditId(sim.id);
      setFormData({
        operator_type: sim.operator_type || sim.operator || "Dish TV",
        sim_number: sim.sim_number,
        sim_id: sim.sim_id || "",
        password: sim.password || "",
        wallet_password: sim.wallet_password || "",
        company_id: sim.company_id || "1",
        organization: sim.organization || "DISHTV",
        is_primary_recharge: sim.is_primary_recharge || false,
        notes: sim.notes || "",
      });
    } else {
      setEditId(null);
      setFormData({
        operator_type: "Dish TV", sim_number: "", sim_id: "", password: "",
        wallet_password: "", company_id: "1", organization: "DISHTV",
        is_primary_recharge: false, notes: ""
      });
    }
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/admin/sim-details/${editId}`, formData);
        toast.success("SIM updated");
      } else {
        await api.post("/admin/sim-details", formData);
        toast.success("SIM added");
      }
      setModalOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to save SIM");
    }
  };

  const remove = async (id) => {
    if (!confirm("Are you sure you want to delete this SIM?")) return;
    try {
      await api.delete(`/admin/sim-details/${id}`);
      toast.success("SIM removed");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // ── Login Flow (Send OTP → Show OTP modal → Validate) ──
  const handleLogin = async (sim) => {
    const opLower = (sim.operator_type || sim.operator || "").toLowerCase();
    if (!opLower.includes("dish")) {
      toast.info("Login is only available for Dish TV SIMs");
      return;
    }
    setActionLoading(prev => ({ ...prev, [sim.id]: 'login' }));
    try {
      const res = await api.post(`/admin/sim-details/${sim.id}/send-otp`);
      toast.success(res.data.message || "OTP sent to SIM number");
      setOtpModal(sim);
      setOtp("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to send OTP");
    } finally {
      setActionLoading(prev => ({ ...prev, [sim.id]: null }));
    }
  };

  const handleValidateOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error("Enter the OTP"); return; }
    setOtpLoading(true);
    try {
      const res = await api.post(`/admin/sim-details/${otpModal.id}/validate-otp`, { otp });
      toast.success(res.data.message || "SIM login successful!");
      setOtpModal(null);
      setOtp("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "OTP validation failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpModal) return;
    setOtpLoading(true);
    try {
      await api.post(`/admin/sim-details/${otpModal.id}/send-otp`);
      toast.success("OTP resent");
    } catch (e) {
      toast.error("Failed to resend OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Check Balance ──
  const handleCheckBalance = async (sim) => {
    setActionLoading(prev => ({ ...prev, [sim.id]: 'balance' }));
    try {
      const res = await api.post(`/admin/sim-details/${sim.id}/check-balance`);
      toast.success(`Balance: ₹${res.data.balance}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 400 || status === 401) {
        // Not logged in or session expired — show warning, refresh to see updated status
        toast.warning(typeof detail === 'string' ? detail : "SIM session issue. Please login again.");
        load();
      } else {
        toast.error(typeof detail === 'string' ? detail : "Failed to check balance");
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [sim.id]: null }));
    }
  };

  const handleSetPrimary = async (simId) => {
    try {
      await api.post(`/admin/sim-details/${simId}/set-primary`);
      toast.success("Set as primary recharge SIM");
      load();
    } catch (e) {
      toast.error("Failed to set primary SIM");
    }
  };

  const statusTone = (s) => {
    if (s === 'logged_in') return 'success';
    if (s === 'active') return 'info';
    if (s === 'session_expired') return 'danger';
    if (s === 'not_logged_in') return 'warn';
    return 'neutral';
  };

  const statusLabel = (s) => {
    if (s === 'logged_in') return 'Logged In';
    if (s === 'session_expired') return 'Session Expired';
    if (s === 'not_logged_in') return 'Not Logged In';
    if (s === 'active') return 'Active';
    if (s === 'inactive') return 'Inactive';
    return s;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-brand-primary" /> SIM Details
          </h3>
          <p className="text-slate-500 text-sm mt-1">Manage Lapu SIMs for recharge operations. Login, check balance, and track status.</p>
        </div>
        <button onClick={() => openModal()} className="bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold text-sm px-4 py-2 rounded-md flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add SIM
        </button>
      </div>

      {/* SIM Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sims.length === 0 ? (
          <div className="col-span-full py-12 text-center text-slate-500 border border-dashed border-slate-300 rounded-xl bg-slate-50">
            No SIM details configured yet. Click "Add SIM" to get started.
          </div>
        ) : (
          sims.map(s => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow relative group">
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold text-lg text-slate-800">{s.operator_type || s.operator}</div>
                    {s.is_primary_recharge && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3 fill-amber-500" /> PRIMARY
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-sm text-slate-500 mt-0.5">{s.sim_number}</div>
                </div>
                <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
              </div>

              {/* SIM ID (masked) */}
              {s.sim_id && (
                <div className="text-xs text-slate-400 mb-1">
                  SIM ID: <span className="font-mono">{s.sim_id.length > 4 ? '••••' + s.sim_id.slice(-4) : s.sim_id}</span>
                </div>
              )}

              {/* Balance + Last Login */}
              <div className="flex items-end justify-between mt-4">
                <div>
                  <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Balance</div>
                  <div className="font-mono font-bold text-xl text-slate-800">₹{parseFloat(s.balance || 0).toFixed(2)}</div>
                </div>
                {s.last_login && (
                  <div className="text-[10px] text-slate-400">
                    Last login: {s.last_login?.slice(0, 16).replace('T', ' ')}
                  </div>
                )}
              </div>

              {/* Notes */}
              {s.notes && <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 line-clamp-2">{s.notes}</div>}

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                {/* Login */}
                <button
                  onClick={() => handleLogin(s)}
                  disabled={!!actionLoading[s.id]}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 transition-colors disabled:opacity-50"
                >
                  {actionLoading[s.id] === 'login' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
                  Login
                </button>

                {/* Check Balance (Disabled for now) */}
                <button
                  disabled={true}
                  title="This feature is coming soon"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-green-50/50 text-green-700/50 cursor-not-allowed transition-colors"
                >
                  <Wallet className="w-3.5 h-3.5" />
                  Check Balance (Soon)
                </button>

                {/* Set Primary */}
                {!s.is_primary_recharge && (
                  <button
                    onClick={() => handleSetPrimary(s.id)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    <Star className="w-3.5 h-3.5" /> Set Primary
                  </button>
                )}

                {/* Edit / Delete */}
                <div className="ml-auto flex gap-1">
                  <button onClick={() => openModal(s)} className="p-2 text-slate-400 hover:text-brand-primary hover:bg-slate-50 rounded-md transition-colors">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(s.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Add / Edit SIM Modal ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModalOpen(false)}>
          <form onSubmit={save} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <h4 className="font-heading font-bold text-xl text-brand-nav mb-6">{editId ? 'Edit SIM' : 'Add SIM'}</h4>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Operator Type *</label>
                <select required value={formData.operator_type} onChange={e => setFormData({...formData, operator_type: e.target.value})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  {OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">SIM Number *</label>
                <input required type="tel" maxLength={10} value={formData.sim_number}
                  onChange={e => setFormData({...formData, sim_number: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" placeholder="10-digit mobile number" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">SIM ID *</label>
                <input required type="text" value={formData.sim_id}
                  onChange={e => setFormData({...formData, sim_id: e.target.value})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" placeholder="Operator SIM / Subscriber ID" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password *</label>
                <input required type="password" value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" placeholder="Operator portal password" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Wallet Password <span className="text-slate-400 font-normal">(for recharge API)</span></label>
                <input type="password" value={formData.wallet_password}
                  onChange={e => setFormData({...formData, wallet_password: e.target.value})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" placeholder="Wallet/entity password" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Company ID</label>
                  <input type="text" value={formData.company_id}
                    onChange={e => setFormData({...formData, company_id: e.target.value})}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Organization</label>
                  <input type="text" value={formData.organization}
                    onChange={e => setFormData({...formData, organization: e.target.value})}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="DISHTV" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea rows={2} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="Additional details..." />
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button type="button" onClick={() => setModalOpen(false)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-md px-4 py-2 font-semibold transition-colors">Save SIM</button>
            </div>
          </form>
        </div>
      )}

      {/* ── OTP Validation Modal ── */}
      {otpModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setOtpModal(null)}>
          <form onSubmit={handleValidateOtp} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading font-bold text-xl text-brand-nav">Enter OTP</h4>
              <button type="button" onClick={() => setOtpModal(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              OTP has been sent to <strong className="font-mono">{otpModal.sim_number}</strong>. Enter it below to complete login.
            </p>

            <div className="mb-4">
              <input
                required autoFocus type="text" maxLength={6} value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full border border-slate-200 rounded-lg px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                placeholder="• • • • • •"
              />
            </div>

            <button type="submit" disabled={otpLoading || otp.length < 4}
              className="w-full bg-brand-primary hover:bg-brand-primaryHover text-white rounded-lg px-4 py-3 font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Verify & Login
            </button>

            <button type="button" onClick={handleResendOtp} disabled={otpLoading}
              className="w-full mt-3 text-sm font-medium text-brand-primary hover:underline disabled:opacity-50">
              Resend OTP
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
